import { CheckListIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { Row } from "views/applications-id-report/components/Row";
import { useState } from "react";
import { Property } from "types/landlord/address";
import { SectionCard } from "shared/app/SectionCard";
import { formatPhone } from "shared/utils";
import {
  BrandingFields,
  type BrandingLandlordFieldsPmsSynced
} from "shared/app/BrandingForm";
import { useFormTyped } from "shared/hooks";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import addressApi from "api/landlord/address";
import { getErrorMessage } from "utils/error";
import * as yup from "yup";
import { Switch, SwitchSize } from "shared/components/forms/Switch";
import { useAuthContext } from "providers/Auth";
import { PartnerFeatures, PropertyKind } from "shared/types";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { generatePrimaryPalette } from "shared/app/BrandingForm/components/ColorPicker/untils";
import { ApplyBrandingModal } from "./ApplyBrandingToProperties/ApplyBrandingModal";

const sessionKey = `${Date.now()}-${crypto.randomUUID()}`;

// A landlord field is PMS-synced when the PMS has a value for it and the partner
// hasn't explicitly overridden it — mirrors Boom::Property.branding_landlord_field_pms_synced?
const isLandlordFieldPmsSynced = (
  pmsValues: Property["pms_values"],
  pmsOverrides: Property["pms_overrides"],
  field: "landlord_name" | "landlord_phone" | "landlord_email"
) => Boolean(pmsValues?.[field]) && pmsOverrides?.[field] !== false;

const getPmsSyncedFields = (
  property: Property
): BrandingLandlordFieldsPmsSynced => ({
  landlord_name: isLandlordFieldPmsSynced(
    property.pms_values,
    property.pms_overrides,
    "landlord_name"
  ),
  landlord_phone: isLandlordFieldPmsSynced(
    property.pms_values,
    property.pms_overrides,
    "landlord_phone"
  ),
  landlord_email: isLandlordFieldPmsSynced(
    property.pms_values,
    property.pms_overrides,
    "landlord_email"
  )
});

interface EditBrandingFormProps {
  onClose: () => void;
  onApplyClick: () => void;
  propertyId: number;
  showLandlordFields: boolean;
  canApplyToMultiple: boolean;
  pmsSyncedFields: BrandingLandlordFieldsPmsSynced;
  initialValues: {
    enabled?: boolean;
    logo?: string;
    primary_color?: string;
    landlord_name?: string;
    landlord_phone?: string;
    landlord_email?: string;
    palette?: Record<string, string>;
  };
}

const brandingValidationSchema = yup.object({
  enabled: yup.boolean(),
  logo: yup.string().optional(),
  primary_color: yup.string().when("enabled", {
    is: true,
    then: (schema) => schema.required("The color is required"),
    otherwise: (schema) => schema.optional()
  }),
  landlord_name: yup
    .string()
    .max(100, "Must be at most 100 characters")
    .optional(),
  landlord_phone: yup
    .string()
    .test(
      "phone-length",
      "Phone number must be exactly 10 characters",
      (value) => !value || value.length === 10
    )
    .optional(),
  landlord_email: yup
    .string()
    .email("Must be a valid email address")
    .optional(),
  palette: yup.object().optional()
});

const BRANDING_DIRTY_KEYS = [
  "enabled",
  "logo",
  "primary_color",
  "landlord_name",
  "landlord_phone",
  "landlord_email"
] as const;

// The bulk apply reads the SAVED branding server-side, so unsaved edits must not be appliable.
const isBrandingDirty = (
  values: EditBrandingFormProps["initialValues"],
  initialValues: EditBrandingFormProps["initialValues"]
) => BRANDING_DIRTY_KEYS.some((key) => values[key] !== initialValues[key]);

const getBrandingSwitchDescription = ({
  enabled,
  dictionary
}: {
  enabled: boolean;
  dictionary: ReturnType<typeof useUnitsPropertiesDictionary>;
}): string => {
  if (enabled)
    return `The ${dictionary.property} and all associated ${dictionary.units} will have separate branding from the company`;

  return "Branding will be set to match the company branding";
};

const EditBrandingForm = ({
  onClose,
  onApplyClick,
  propertyId,
  showLandlordFields,
  canApplyToMultiple,
  pmsSyncedFields,
  initialValues
}: EditBrandingFormProps) => {
  const queryClient = useQueryClient();
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [hasNewLogoUpload, setHasNewLogoUpload] = useState(false);
  const dictionary = useUnitsPropertiesDictionary();

  const { Property } = dictionary;

  const { register, onSubmit, values, errors } = useFormTyped(
    initialValues,
    brandingValidationSchema
  );

  const isDirty = isBrandingDirty(values, initialValues);

  const { mutate: updateBranding, isPending } = useMutation({
    mutationFn: (vals: EditBrandingFormProps["initialValues"]) => {
      return addressApi.editProperty({
        propertyId,
        // Only sent when a logo was actually (re)uploaded this session — the API's
        // clean_up_media_cache step (partner/property/update.rb) runs unconditionally
        // whenever this key is present and calls branding_with_logo.dig(:logo, :asset, :id),
        // which raises if branding.logo is the previously-rendered URL string rather than a
        // fresh gid (i.e. any save that doesn't touch the logo). Gating on a real new upload
        // still evicts the presigned asset from its tracking cache so it survives
        // Listing::Asset::CleanupPresignedJob (CRM-1027) without breaking untouched-logo saves.
        ...(hasNewLogoUpload ? { media_session_key: sessionKey } : {}),
        branding: {
          enabled: vals.enabled,
          logo: vals.logo,
          primary_color: vals.primary_color,
          landlord_name: vals.landlord_name,
          landlord_phone: vals.landlord_phone,
          landlord_email: vals.landlord_email,
          palette: vals.palette
        }
      });
    },
    onSuccess: () => {
      toast.success("Branding updated successfully");
      queryClient.invalidateQueries({
        queryKey: ["properties", String(propertyId)]
      });
      onClose();
    },
    onError: (error) => {
      toast.error(`Failed to update branding: ${getErrorMessage(error)}`);
    }
  });

  const handleSubmit = onSubmit({
    onSuccess: (vals) => updateBranding(vals),
    onError: () => {
      toast.error("Form validation failed");
    }
  });

  return (
    <SectionCard
      title="Branding"
      footer={
        <div className="flex items-center justify-between w-full">
          <SaveCancelButtons
            onSave={(e) => handleSubmit(e)}
            onCancel={onClose}
            saveLoading={isPending}
            saveDisabled={
              isLogoUploading || Object.values(errors).some(Boolean)
            }
          />
          {values.enabled && canApplyToMultiple && (
            <Button
              HugeIcon={CheckListIcon}
              className="flex items-center gap-2 text-base font-bold"
              disabled={isDirty}
              tooltip={
                isDirty
                  ? "Save your branding changes first — only saved branding is applied to other properties"
                  : undefined
              }
              variant={ButtonVariant.TEXT}
              onClick={onApplyClick}
            >
              Apply to multiple {dictionary.properties}
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-6">
        <div className="pb-6 border-b border-alpha-40 flex items-start gap-3">
          <Switch
            checked={values.enabled ?? false}
            onChange={(v) => register("enabled").onChange(v)}
            size={SwitchSize.BASE}
          />
          <div>
            <p className="text-sm font-medium text-content-700">
              {Property} level branding is{" "}
              {values.enabled ? "enabled" : "disabled"}
            </p>
            <p className="text-sm text-content-200">
              {getBrandingSwitchDescription({
                enabled: Boolean(values.enabled),
                dictionary
              })}
            </p>
          </div>
        </div>

        {values.enabled && (
          <BrandingFields
            register={register}
            sessionKey={sessionKey}
            onUploadingChange={setIsLogoUploading}
            onLogoUploaded={setHasNewLogoUpload}
            showLandlordFields={showLandlordFields}
            pmsSyncedFields={pmsSyncedFields}
          />
        )}
      </div>
    </SectionCard>
  );
};

export const BrandingSection = ({ property }: { property: Property }) => {
  const { type, partner, partnerFeatures } = useAuthContext();
  const [isEditing, setIsEditing] = useState(false);
  const [isApplyOpen, setIsApplyOpen] = useState(false);
  const dictionary = useUnitsPropertiesDictionary();
  const { Property } = dictionary;
  const queryClient = useQueryClient();

  const showLandlordFields =
    (property.kind &&
      [PropertyKind.SingleFamily, PropertyKind.MultiFamily].includes(
        property.kind
      )) ||
    type.isPmsListingSyncEnabled;

  // Bulk apply groups properties by owner, and the owner field only exists for partners with the property_owner feature
  const canApplyToMultiple = partnerFeatures.includes(
    PartnerFeatures.PropertyOwner
  );

  const partnerPrimaryColor = partner?.colors?.primary?.["500"];
  const propertyQueryKey = ["properties", String(property.id)];

  const { mutate: toggleEnabled, isPending: isToggling } = useMutation({
    mutationFn: (enabled: boolean) => {
      const needsColor = enabled && !property.branding?.primary_color;
      const defaultColor = partnerPrimaryColor || "#e6007e";
      return addressApi.editProperty({
        propertyId: property.id,
        branding: {
          enabled,
          ...(needsColor
            ? {
                primary_color: defaultColor,
                palette: generatePrimaryPalette(defaultColor)
              }
            : {})
        }
      });
    },
    onSuccess: (_, enabled) => {
      const needsColor = enabled && !property.branding?.primary_color;
      const defaultColor = partnerPrimaryColor || "#e6007e";
      const colorPatch = needsColor
        ? {
            primary_color: defaultColor,
            palette: generatePrimaryPalette(defaultColor)
          }
        : {};
      queryClient.setQueryData<Property>(propertyQueryKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          branding: { ...(old.branding ?? {}), enabled, ...colorPatch }
        };
      });
      queryClient.invalidateQueries({ queryKey: propertyQueryKey });
    },
    onError: (error) => {
      toast.error(`Failed to update branding: ${getErrorMessage(error)}`);
    }
  });

  if (isEditing)
    return (
      <>
        <EditBrandingForm
          onClose={() => setIsEditing(false)}
          onApplyClick={() => setIsApplyOpen(true)}
          propertyId={property.id}
          showLandlordFields={showLandlordFields}
          canApplyToMultiple={canApplyToMultiple}
          pmsSyncedFields={getPmsSyncedFields(property)}
          initialValues={{
            enabled: property.branding?.enabled ?? false,
            logo:
              typeof property.branding?.logo === "string"
                ? property.branding.logo
                : property.branding?.logo?.asset?.url,
            primary_color:
              property.branding?.primary_color ||
              partner?.colors?.primary?.["500"],
            landlord_name: property.branding?.landlord_name,
            landlord_phone:
              property.branding?.landlord_phone?.replace(/\D/g, "") ||
              undefined,
            landlord_email: property.branding?.landlord_email,
            palette:
              property.branding?.palette ||
              (partner?.colors?.primary?.["500"]
                ? generatePrimaryPalette(partner.colors.primary["500"])
                : undefined)
          }}
        />
        <ApplyBrandingModal
          opened={isApplyOpen && canApplyToMultiple}
          onClose={() => setIsApplyOpen(false)}
          sourceProperty={property}
          showLandlordFields={showLandlordFields}
        />
      </>
    );

  const enabled = property.branding?.enabled ?? false;

  return (
    <SectionCard
      title="Branding"
      headerExtra={
        enabled ? (
          <Button
            size={ButtonSize.SM}
            variant={ButtonVariant.BASE}
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        ) : undefined
      }
    >
      <div className={enabled ? "space-y-4" : ""}>
        <div
          className={`flex items-start gap-3${enabled ? " pb-4 border-b border-alpha-40" : ""}`}
        >
          <Switch
            checked={enabled}
            size={SwitchSize.BASE}
            disabled={isToggling}
            onChange={(v) => toggleEnabled(v)}
          />
          <div>
            <p className="text-sm font-medium text-content-700">
              {Property} level branding is {enabled ? "enabled" : "disabled"}
            </p>
            <p className="text-sm text-content-200">
              {getBrandingSwitchDescription({ enabled, dictionary })}
            </p>
          </div>
        </div>

        {enabled && (
          <div className="space-y-4">
            <Row
              title="Primary color"
              value={property.branding?.primary_color}
            />
            {showLandlordFields && (
              <>
                <Row
                  title="Landlord name"
                  value={property.branding?.landlord_name}
                />
                <Row
                  title="Landlord phone"
                  value={formatPhone(property.branding?.landlord_phone || "")}
                />
                <Row
                  title="Landlord email"
                  value={property.branding?.landlord_email}
                />
              </>
            )}
          </div>
        )}
      </div>
    </SectionCard>
  );
};
